import { ApiService } from './api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { TokenService } from './token.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthUserModel } from 'projects/core/src/lib/models/authuser.model';
import { SlotModel } from '../models/slot.model';
import { GroupModel } from '../models/group.model';
import { TimerType } from '../models/acstatus/ac-status.type';

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {

  private user = new BehaviorSubject<AuthUserModel>(null);
  private loginState = new BehaviorSubject<boolean>(false);
  public user$ = this.user.asObservable();
  public loginState$ = this.loginState.asObservable();

  constructor(
    private token: TokenService,
    private api: ApiService,
    private router: Router
  ) {
    this.refreshAuthState();
  }

  public getAuthUser(): Observable<AuthUserModel> {
    return this.user.asObservable();
  }

  public login(token: string, route: string = ''): void {
    this.token.setToken(token);
    this.refreshAuthState();
    this.router.navigate([route]);
  }

  public logout(withRouting: boolean = true): void {
    if (this.isLoggedIn()) {
      const route = (this.isModerator()) ? '/login' : '/bye';
      this.api.logout().subscribe({
        next: () => {
        }
      }).add(() => {
        this.token.removeToken();
        this.refreshAuthState();
        if (withRouting === true) {
          this.router.navigate([route]);
        }
      });
    } else {
      this.refreshAuthState();
      if (withRouting === true) {
        this.router.navigate(['/login']);
      }
    }
  }

  public isLoggedIn(): boolean {
    return this.token.isLoggedIn();
  }

  public isModerator(): boolean {
    return this.token.isModerator();
  }

  public isObserver(): boolean {
    return this.token.isObserver() && !this.token.isModerator();
  }

  public isCandidate(): boolean {
    return !this.isObserver() && !this.isModerator();
  }

  public getUserType(): string {
    return (this.isModerator()) ? 'moderator' : ((this.isObserver()) ? 'observer' : 'candidate');
  }

  public getHashedId(): string {
    return this.token.getHashedId();
  }

  public isMe(hash: string): boolean {
    return this.getHashedId() === hash;
  }

  public isSilentObserver(): boolean {
    return this.token.isSilentObserver();
  }

  private refreshAuthState(): void {
    this.user.next(this.token.getAuthUser());
    this.loginState.next(this.isLoggedIn());
  }

  // manage slot access

  public canOpenSlot(hash: string, slot: SlotModel, group: GroupModel = null, inObserverVisibleTime: boolean = false): boolean {
    const inGroup = (group !== null && slot.ac?.isGroup()) ? group.isInGroup(this.getHashedId()) : true;
    const slotRunning = slot?.isRunningOrPaused(hash);
    return this.isModerator() ||
      (this.isObserver() && inGroup && (
        slotRunning || inObserverVisibleTime
      )) ||
      (this.isCandidate() && inGroup && slotRunning);
  }

  public canOpenRating(group: GroupModel = null): boolean {
    const inGroup = (group !== null) ? group.isInGroup(this.getHashedId()) : true;
    return this.isModerator() ||
      (
        this.isObserver() && inGroup
      );
  }

  public canControlSlot(group: GroupModel = null): boolean {
    const isGroupMod = (group !== null) ? group.isGroupModerator(this.getHashedId()) : false;
    return this.isModerator() || isGroupMod;
  }

  public isSlotVisible(slot: SlotModel, group: GroupModel = null): boolean {
    const isGroupMod = (group !== null) ? group.isGroupModerator(this.getHashedId()) : false;
    return this.isModerator() || isGroupMod ||
      (this.isObserver() && slot.for_observer === true) ||
      (this.isCandidate() && slot.for_candidate === true);
  }

}
